import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CharacterRelationsWithMainItems,
  DefaultMira,
  Favorite, ItemCastCharacterRelation, ItemPersonRelation, ItemRelation, ItemRelationsWithMainItems,
  ItemType,
  MainItem, MainItemBasic, MainType, PersonRelationsWithMainItems,
  PropertyValueValue
} from '@irohalab/mira-sdk-angular';
import { combineLatest, filter, map, Subscription, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { KeyValuePipe, NgClass } from '@angular/common';
import { UIResponsiveImageWrapper } from '@irohalab/deneb-ui';
import { NavbarComponent } from '../navbar/navbar.component';
import { UserActionComponent } from '../user-action/user-action.component';
import { UserAccountService } from '../user-account.service';
import { UserAccount } from '../../entity/UserAccount';
import { UserAccountType } from '../../entity/UserAccountType';
import { EpisodeListComponent } from './episode-list/episode-list.component';
import { OverviewComponent } from './overview/overview.component';
import { StaffListComponent } from './staff-list/staff-list.component';
import { ItemListComponent } from './item-list/item-list.component';
import { PersonItemListComponent } from './person-item-list/person-item-list.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { environment } from '../../environments/environment';

const ITEM_ANIME_PROPERTIES = ['别名', '话数', '放送开始', '放送星期', '原作', '导演', '脚本', '分镜', '演出', '音乐',
  '系列构成', '美术监督', '色彩设计', '总作画监督', '作画监督', '摄影监督', '企画', '製作', '音响监督', '音响', '执行制片人', '制片人', '动画制作', '播放电视台', 'Copyright', '官方网站'];
const ITEM_BOOK_SERIES_PROPERTIES = ['作者', '出版社', '别名', '发售日', '开始', '作客刊载'];
const ITEM_BOOK_PROPERTIES = ['作者', '出版社', '发售日', 'ISBN'];
const ITEM_REAL_PROPERTIES = ['别名', '集数', '导演', '编剧', '主演', '配角', '音乐', '开始', '国家/地区', '语言', '制作', 'imdb_id', '官方网站'];
const ITEM_MUSIC_PROPERTIES = ['别名', '艺术家', '作曲', '作词', '编曲', '版本特性', '发售日期', '价格', '播放时长', '厂牌', '出版方', '碟片数量'];
const ITEM_GAME_PROPERTIES = ['开发', '发行', '音乐', '人物设定', '剧本', '原画', 'SD原画', '主题歌作曲', '主题歌作词', '主题歌演出', '平台', '游戏类型', '游戏引擎', '发行日期', '官方网站'];
const PERSON_BASIC_PROPERTIES = ['别名', '性别', '生日', '血型', '身高', '体重', '引用来源', '出身地', '事务所'];
const CHARACTER_BASIC_PROPERTIES = ['别名', '性别', '生日', '血型', '身高', '引用来源'];

type ItemProperty = {bgmKey: string, value: PropertyValueValue, isArrayValue: boolean};

@Component({
    selector: 'app-item-detail',
    imports: [
    UIResponsiveImageWrapper,
    NavbarComponent,
    UserActionComponent,
    EpisodeListComponent,
    NgClass,
    OverviewComponent,
    StaffListComponent,
    ItemListComponent,
    KeyValuePipe,
    PersonItemListComponent,
    SidebarComponent
],
    templateUrl: './item-detail.component.html',
    styleUrl: './item-detail.component.less'
})
export class ItemDetailComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  mainItem!: MainItem;
  user!: UserAccount;
  favorite!: Favorite;

  selectedTab: string = 'overview';
  basicProperties: ItemProperty[] = [];
  extraProperties: ItemProperty[] = [];

  itemRelations!: ItemRelation[];
  itemPersonRelations!: ItemPersonRelation[];
  itemCastCharacterRelations!: ItemCastCharacterRelation[];
  relatedMainItems!: MainItemBasic[];

  get isAdminUser(): boolean {
    return this.user ? (this.user.type === 'Admin' || this.user.type === 'SuperAdmin') : false;
  }

  adminUrl!: string;

  get isEpisodesEnabled(): boolean {
    return !!this.mainItem
      && (this.mainItem.type === ItemType.Anime || this.mainItem.type === ItemType.Music || this.mainItem.type === ItemType.RealWorld);
  }

  get isStaffEnabled(): boolean {
    return !!this.mainItem && this.itemPersonRelations?.length > 0 && this.mainItem.mainType === MainType.Item;
  }

  get isRelatedItemsEnabled(): boolean {
    return !!this.mainItem && this.itemRelations?.length > 0 && this.mainItem.mainType === MainType.Item;
  }

  get isPersonItemsEnabled(): boolean {
    return !!this.mainItem && this.itemPersonRelations?.length > 0 && this.mainItem.mainType === MainType.Person;
  }

  get bgmURL(): string {
    if (!this.mainItem) {
      return '';
    }

    switch (this.mainItem.mainType) {
      case MainType.Item:
        return `https://bgm.tv/subject/${this.mainItem.bgmId}`;
      case MainType.Person:
        return `https://bgm.tv/person/${this.mainItem.bgmId}`;
      case MainType.Character:
        return `https://bgm.tv/character/${this.mainItem.bgmId}`;
      default:
        return `https://bgm.tv/subject/${this.mainItem.bgmId}`;
    }
  }

  constructor(private miraApiService: DefaultMira,
              private userAccountService: UserAccountService,
              private route: ActivatedRoute,
              private router: Router) {
  }

  ngOnInit(): void {
    const itemIdObservable = this.route.paramMap
      .pipe(
        filter(paramMap => paramMap.has('id')),
        map(paramMap => {
          return paramMap.get('id') as string;
        }));
    this.subscription.add(
      itemIdObservable.pipe(
        switchMap((itemId) => {
          return this.miraApiService.getMainItemById(String(itemId), 'true', 'true')
        }),
        tap((item: MainItem) => {
          this.adminUrl = `${environment.adminUIBasePath}/mainitem/${item.id}`;
          this.mainItem = item;
        }),
        switchMap((item: MainItem) => {
          switch (item.mainType) {
            case MainType.Item:
              return this.miraApiService.getRelationsOfItem(item.id);
            case MainType.Person:
              return this.miraApiService.getRelationsOfPerson(item.id);
            case MainType.Character:
              return this.miraApiService.getRelationsOfCharacter(item.id);
            default:
              // mainType not support, this is unexpected
              console.log('item mainType is not supported', item);
              throw new Error('item mainType is not supported');
          }
        })
      )
        .subscribe({
          next: (data: ItemRelationsWithMainItems | PersonRelationsWithMainItems | CharacterRelationsWithMainItems) => {
            this.relatedMainItems = data.mainItems;
            switch (this.mainItem.mainType) {
              case MainType.Item:
                const itemRelations = data as ItemRelationsWithMainItems;
                this.itemRelations = itemRelations.relatedItems;
                this.itemPersonRelations = itemRelations.relatedPersons;
                this.itemCastCharacterRelations = itemRelations.relatedCharacters;
                break;
              case MainType.Person:
                const personRelations = data as PersonRelationsWithMainItems;
                this.itemPersonRelations = personRelations.relatedItems;
                this.itemCastCharacterRelations = personRelations.relatedCharacters;
                break;
              case MainType.Character:
                const characterRelations = data as CharacterRelationsWithMainItems;
                this.itemCastCharacterRelations = characterRelations.relatedItems;
                break;
              default:
                // do nothing
            }
            this.processProperties();
          },
          error: (err) => {
            console.log(err);
            this.router.navigate(['error'], {skipLocationChange: true}).then(() => {
            });
          }
        })
    );

    this.subscription.add(
      combineLatest([
        itemIdObservable,
        this.userAccountService.user.pipe(
          filter(user => user.type !== UserAccountType.Anonymous)
        )
      ])
        .pipe(
          switchMap(([itemId, _user]) => {
            return this.miraApiService.getFavoriteByMainItemId(itemId);
          })
        )
        .subscribe({
          next: (favorite) => {
            this.favorite = favorite;
          },
          error: (error: any) => {
            console.log(error);
          }
        })
    )

    this.subscription.add(
      this.userAccountService.user
        .subscribe({
          next: (data) => {
            // console.log(data);
            this.user = data;
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  changeTab(tab: string) {
    this.selectedTab = tab;
  }

  isString(v: any): boolean {
    return typeof v === 'string';
  }

  isKVPair(v: any): boolean {
    return typeof v === 'object' && v.k && v.v;
  }

  private processProperties(): void {
    const properties = Object.assign({}, this.mainItem.properties);
    let basicPropertiesKeyList: string[];
    const basicProperties: ItemProperty[] = [];
    switch (this.mainItem.mainType) {
      case MainType.Item:
        switch (this.mainItem.type) {
          case ItemType.Anime:
            basicPropertiesKeyList = ITEM_ANIME_PROPERTIES;
            break;
          case ItemType.RealWorld:
            basicPropertiesKeyList = ITEM_REAL_PROPERTIES;
            break;
          case ItemType.Book:
            basicPropertiesKeyList = this.mainItem.isCollection ? ITEM_BOOK_SERIES_PROPERTIES : ITEM_BOOK_PROPERTIES;
            break;
          case ItemType.Music:
            basicPropertiesKeyList = ITEM_MUSIC_PROPERTIES;
            break;
          case ItemType.Game:
            basicPropertiesKeyList = ITEM_GAME_PROPERTIES;
            break;
          default:
            basicPropertiesKeyList = [];
        }
        break;
      case MainType.Person:
        basicPropertiesKeyList = PERSON_BASIC_PROPERTIES;
        break;
      case MainType.Character:
        basicPropertiesKeyList = CHARACTER_BASIC_PROPERTIES;
        break;
      default:
        console.log('unsupported mainType');
        basicPropertiesKeyList = [];
    }

    for (const bgmKey of basicPropertiesKeyList) {
      const propKey = Object.keys(properties).find(key => {
        return properties[key].bgmKey === bgmKey;
      });
      if (propKey) {
        const propValue = properties[propKey].value;
        basicProperties.push({bgmKey, value: propValue, isArrayValue: Array.isArray(propValue)});
        delete properties[propKey];
      }
    }
    this.basicProperties = basicProperties;
    this.extraProperties = Object.values(properties).map((prop) => {
      return {
        bgmKey: prop.bgmKey,
        value: prop.value,
        isArrayValue: Array.isArray(prop.value)
      } as ItemProperty;
    })
  }
}
